import { Link } from "@tanstack/react-router";
import {
  ArrowRight,
  CheckCircle2,
  BookOpen,
  Sparkles,
  XCircle,
  Info,
  AlertTriangle,
  Quote,
  TrendingUp,
  Calendar,
  Phone,
  Mail,
  MapPin,
} from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { ResponsiveImg } from "@/components/ResponsiveImg";
import logo from "@/assets/bbn-logo.jpg.asset.json";

export type FaqItem = { q: string; a: string };

export interface GuideMeta {
  /** Vinkel-bild (hero-bakgrund). Lämna tom för gradient-fallback. */
  heroImage?: string;
  /** Beskrivande alt-text för heroImage. Default = title. */
  heroImageAlt?: string;
  category: string;
  title: string;
  /** TL;DR — 2–3 meningar AI-modeller plockar upp ordagrant. */
  tldr: string;
  readTimeMin: number;
  /** Sidans path för canonical + Article-schema, t.ex. "/guider/solfilm-kontor". */
  path: string;
  /** Datum för senaste uppdatering, ISO. */
  updated: string;
  /** Datum då guiden först publicerades, ISO. Faller tillbaka till updated. */
  published?: string;
  faqs?: FaqItem[];
  /** Relaterade interna länkar i botten. */
  related?: { to: string; label: string }[];
  /** Valfri HowTo — exporteras som schema.org/HowTo JSON-LD. */
  howTo?: { name: string; steps: { name: string; text: string }[] };
}

function formatSvDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("sv-SE", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

export function GuideHero({ meta }: { meta: GuideMeta }) {
  return <GuideHeroInner meta={meta} />;
}

function GuideHeroInner({ meta }: { meta: GuideMeta }) {
  return (
    <section className="relative pt-24 pb-10 md:pt-40 md:pb-16 bg-navy overflow-hidden">
      {meta.heroImage && (
        <div className="absolute inset-0">
          <ResponsiveImg
            src={meta.heroImage}
            alt={meta.heroImageAlt ?? meta.title}
            priority="lcp"
            className="h-full w-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy/85 via-navy/60 to-navy/30" aria-hidden />
          <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-navy to-transparent" aria-hidden />
        </div>
      )}
      <div className="relative mx-auto max-w-4xl px-5 md:px-10" style={{ textShadow: meta.heroImage ? "0 2px 16px rgba(0,0,0,0.5)" : undefined }}>
        <div className="flex flex-wrap items-center gap-3 text-xs uppercase tracking-[0.25em] text-gold-bright font-semibold mb-4">
          <BookOpen size={14} />
          <span>Guide · {meta.category}</span>
          <span className="text-cream/70">·</span>
          <span className="text-cream/80">{meta.readTimeMin} min läsning</span>
        </div>
        <h1 className="font-display text-cream text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
          {meta.title}
        </h1>
        <p className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-cream/70" style={{ textShadow: "none" }}>
          <span className="inline-flex items-center gap-1.5">
            <Calendar size={13} />
            Publicerad <time dateTime={meta.published ?? meta.updated}>{formatSvDate(meta.published ?? meta.updated)}</time>
          </span>
          {meta.published && meta.published !== meta.updated && (
            <>
              <span aria-hidden className="text-cream/30">·</span>
              <span>
                Uppdaterad <time dateTime={meta.updated}>{formatSvDate(meta.updated)}</time>
              </span>
            </>
          )}
        </p>
        {/* TL;DR block — det AI-modeller läser först */}
        <aside className="mt-6 rounded-2xl border border-gold/30 bg-gold/10 backdrop-blur-sm p-5 md:p-6" style={{ textShadow: "none" }} data-speakable="tldr">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-gold-bright font-semibold mb-3">
            <Sparkles size={12} /> Sammanfattning
          </div>
          <p className="text-cream/95 text-base md:text-lg leading-relaxed">{meta.tldr}</p>
        </aside>
        <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:items-center" style={{ textShadow: "none" }}>
          <Link
            to="/offert"
            className="btn-glow inline-flex items-center justify-center gap-2 bg-gold text-navy px-6 py-3 rounded-full font-semibold min-h-11"
          >
            Få gratis offert <ArrowRight size={16} />
          </Link>
          <a
            href="tel:+46771502502"
            className="inline-flex items-center justify-center gap-2 text-cream/85 px-4 py-3 font-medium hover:text-gold-bright transition-colors min-h-11"
          >
            Ring 0771 502 502
          </a>
        </div>
      </div>
    </section>
  );
}

/** Innehållsförteckning — läser H2 från närmaste <article> och listar dem om > 5 st. */
export function GuideToc() {
  const [items, setItems] = useState<{ id: string; text: string }[]>([]);
  useEffect(() => {
    const article = document.querySelector("article");
    if (!article) return;
    const h2s = Array.from(article.querySelectorAll("h2"));
    if (h2s.length < 6) return;
    const list = h2s.map((h, i) => {
      const text = (h.textContent ?? "").trim();
      let id = h.id;
      if (!id) {
        id =
          text
            .toLowerCase()
            .replace(/[åä]/g, "a")
            .replace(/ö/g, "o")
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/(^-|-$)/g, "") || `section-${i}`;
        h.id = id;
      }
      return { id, text };
    });
    setItems(list);
  }, []);
  if (items.length === 0) return null;
  return (
    <nav aria-label="Innehåll" className="not-prose mb-10 rounded-2xl border border-border bg-bbn-neutral p-5 md:p-6">
      <p className="text-[11px] uppercase tracking-[0.25em] text-gold font-semibold mb-3">Innehåll</p>
      <ol className="grid gap-1.5 md:grid-cols-2 list-decimal list-inside marker:text-gold marker:font-semibold text-sm">
        {items.map((it) => (
          <li key={it.id}>
            <a href={`#${it.id}`} className="text-foreground/85 hover:text-bbn-primary transition-colors">
              {it.text}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

export function GuideBody({ children }: { children: ReactNode }) {
  return (
    <article className="mx-auto max-w-[44rem] px-5 md:px-10 py-14 md:py-20 prose prose-lg md:prose-xl prose-headings:font-display prose-headings:tracking-tight prose-h2:text-3xl md:prose-h2:text-4xl prose-h2:mt-16 prose-h2:mb-6 prose-h2:text-foreground prose-h2:scroll-mt-24 prose-h3:text-xl md:prose-h3:text-2xl prose-h3:mt-10 prose-h3:mb-3 prose-h3:text-foreground prose-p:text-foreground/90 prose-p:leading-[1.8] prose-p:my-6 prose-strong:text-foreground prose-a:text-bbn-primary hover:prose-a:text-bbn-accent prose-li:text-foreground/90 prose-li:leading-[1.7] prose-li:my-2 prose-ul:my-6 prose-ol:my-6 prose-table:text-base prose-th:text-foreground prose-th:bg-bbn-neutral prose-th:font-semibold prose-td:text-foreground/90 prose-td:py-3 prose-img:rounded-xl prose-img:my-8">
      <GuideToc />
      {children}
    </article>
  );
}

/** Kompakt inline-CTA att droppa in mellan större H2-sektioner. */
export function GuideInlineCta({
  heading = "Redo att räkna på din fastighet?",
  body = "Kostnadsfri uppmätning i Skåne · fast skriftlig offert · 10 års garanti.",
}: {
  heading?: string;
  body?: string;
}) {
  return (
    <aside className="not-prose my-10 rounded-2xl border border-gold/30 bg-gradient-to-br from-navy to-navy/90 p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
      <div className="flex-1">
        <p className="font-display text-cream text-lg md:text-xl leading-snug m-0">{heading}</p>
        <p className="text-cream/75 text-sm mt-1 m-0">{body}</p>
      </div>
      <div className="flex flex-col sm:flex-row gap-2 shrink-0">
        <Link
          to="/offert"
          className="inline-flex items-center justify-center gap-2 bg-gold text-navy px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-gold-bright transition-colors min-h-11"
        >
          Få gratis offert <ArrowRight size={14} />
        </Link>
        <a
          href="tel:+46771502502"
          className="inline-flex items-center justify-center gap-2 text-cream/90 px-4 py-2.5 text-sm font-medium hover:text-gold-bright transition-colors min-h-11"
        >
          <Phone size={14} /> 0771 502 502
        </a>
      </div>
    </aside>
  );
}

/** Förenklad navigering — bara logga + primär CTA. Används av __root på /guider/*. */
export function GuideNav() {
  return (
    <header className="fixed left-0 right-0 top-0 z-[60] bg-navy/95 backdrop-blur-lg border-b border-white/10">
      <nav className="mx-auto max-w-5xl px-5 md:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" aria-label="BBN Nordic hem">
          <img src={logo.url} alt="BBN Nordic" width={160} height={40} className="h-7 md:h-8 w-auto" />
        </Link>
        <div className="flex items-center gap-2">
          <a
            href="tel:+46771502502"
            className="hidden sm:inline-flex items-center gap-1.5 text-cream/80 hover:text-gold-bright text-sm font-medium px-3 py-2 transition-colors"
          >
            <Phone size={14} /> 0771 502 502
          </a>
          <Link
            to="/offert"
            className="btn-glow bg-gold text-navy px-4 md:px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-gold-bright transition-colors"
          >
            Få gratis offert →
          </Link>
        </div>
      </nav>
    </header>
  );
}

/** Förenklad footer — kontaktinfo + copyright. Används av __root på /guider/*. */
export function GuideFooter() {
  return (
    <footer className="bg-bbn-primary text-cream mt-10">
      <div className="mx-auto max-w-5xl px-5 md:px-10 py-10">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <Link to="/" aria-label="BBN Nordic hem">
            <img src={logo.url} alt="BBN Nordic" width={160} height={40} loading="lazy" decoding="async" className="h-9 w-auto opacity-95" />
          </Link>
          <div className="flex flex-col sm:flex-row flex-wrap gap-x-6 gap-y-2 text-sm text-cream/85 font-light">
            <span className="inline-flex items-center gap-2">
              <MapPin size={14} className="text-cream/70" /> Satellitvägen 16, Staffanstorp
            </span>
            <a href="tel:+46771502502" className="inline-flex items-center gap-2 hover:text-cream transition-colors">
              <Phone size={14} className="text-cream/70" /> 0771 502 502
            </a>
            <a href="mailto:info@bbnnordic.se" className="inline-flex items-center gap-2 hover:text-cream transition-colors">
              <Mail size={14} className="text-cream/70" /> info@bbnnordic.se
            </a>
          </div>
        </div>
        <div className="mt-8 pt-5 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-cream/75">
          <p>© {new Date().getFullYear()} BBN Nordic AB · Org.nr 556914-8306</p>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
            <Link to="/guider" className="hover:text-cream transition-colors">Alla guider</Link>
            <span className="text-cream/30" aria-hidden>|</span>
            <Link to="/integritetspolicy" className="hover:text-cream transition-colors">Integritetspolicy</Link>
            <span className="text-cream/30" aria-hidden>|</span>
            <Link to="/cookiepolicy" className="hover:text-cream transition-colors">Cookies</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

export function GuideFaq({ items }: { items: FaqItem[] }) {
  if (!items.length) return null;
  return (
    <section className="bg-bbn-neutral py-16 md:py-20">
      <div className="mx-auto max-w-3xl px-5 md:px-10">
        <p className="section-number mb-3">FAQ</p>
        <h2 className="font-display text-3xl md:text-4xl text-foreground mb-8">Vanliga frågor</h2>
        <dl className="space-y-3">
          {items.map((f) => (
            <details key={f.q} className="group bg-card border border-border rounded-xl p-5 open:shadow-md transition-shadow">
              <summary className="cursor-pointer font-semibold text-foreground flex items-start gap-3 list-none">
                <span className="mt-0.5 text-gold">›</span>
                <span className="flex-1">{f.q}</span>
              </summary>
              <dd className="mt-3 ml-6 text-foreground/80 leading-relaxed">{f.a}</dd>
            </details>
          ))}
        </dl>
      </div>
    </section>
  );
}

export function GuideCta() {
  return (
    <section className="bg-navy text-cream py-16 md:py-20">
      <div className="mx-auto max-w-3xl px-5 md:px-10 text-center">
        <p className="section-number mb-3">Nästa steg</p>
        <h2 className="font-display text-3xl md:text-4xl text-cream mb-4">
          Få kostnadsfri offert
        </h2>
        <p className="text-cream/80 max-w-xl mx-auto mb-8 leading-relaxed">
          Auktoriserad 3M-installatör · hela Sverige · 10 års produktgaranti.
          Vi besöker er kostnadsfritt för att mäta upp och föreslå rätt film.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/offert"
            className="btn-glow inline-flex items-center justify-center gap-2 bg-gold text-navy px-7 py-4 rounded-full font-semibold min-h-12"
          >
            Få gratis offert <ArrowRight size={18} />
          </Link>
          <a
            href="tel:+46771502502"
            className="inline-flex items-center justify-center gap-2 text-cream/85 px-5 py-4 font-medium hover:text-gold-bright transition-colors min-h-12"
          >
            Ring 0771 502 502
          </a>
        </div>
      </div>
    </section>
  );
}

export function GuideRelated({ items }: { items: { to: string; label: string }[] }) {
  if (!items.length) return null;
  return (
    <section className="bg-background py-12 border-t border-border">
      <div className="mx-auto max-w-3xl px-5 md:px-10">
        <p className="section-number mb-4">Läs mer</p>
        <ul className="grid gap-2">
          {items.map((r) => (
            <li key={r.to}>
              <Link
                to={r.to}
                className="group inline-flex items-center gap-2 text-foreground hover:text-bbn-primary transition-colors"
              >
                <ArrowRight size={14} className="text-gold transition-transform group-hover:translate-x-1" />
                {r.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export function CheckList({ items }: { items: string[] }) {
  return (
    <ul className="not-prose space-y-2 my-6">
      {items.map((t) => (
        <li key={t} className="flex items-start gap-3">
          <CheckCircle2 size={18} className="mt-1 shrink-0 text-gold" />
          <span className="text-foreground/85">{t}</span>
        </li>
      ))}
    </ul>
  );
}

/* ============================================================
 * Rich AI-readable widgets — use inside <GuideBody> as not-prose
 * ============================================================ */

export type KeyStat = { value: string; label: string; sub?: string };

/** Sifferruta överst i guider — toppmått som AI och människor scannar direkt. */
export function KeyStats({ items, source }: { items: KeyStat[]; source?: string }) {
  return (
    <aside className="not-prose my-8" aria-label="Nyckeltal">
      <dl className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {items.map((s) => (
          <div
            key={s.label}
            className="rounded-xl border border-border bg-card p-4 md:p-5 text-center"
          >
            <dt className="sr-only">{s.label}</dt>
            <dd className="font-display text-3xl md:text-4xl text-navy leading-none">
              {s.value}
            </dd>
            <p className="mt-2 text-xs uppercase tracking-wider text-foreground/60 font-semibold">
              {s.label}
            </p>
            {s.sub && (
              <p className="mt-1 text-[11px] text-muted-foreground">{s.sub}</p>
            )}
          </div>
        ))}
      </dl>
      {source && (
        <p className="mt-2 text-[11px] text-muted-foreground text-right">Källa: {source}</p>
      )}
    </aside>
  );
}

/** För/Nackdelar — två kolumner, semantiskt parad för AI-tolkning. */
export function ProsCons({
  pros,
  cons,
  prosTitle = "Fördelar",
  consTitle = "Begränsningar",
}: {
  pros: string[];
  cons: string[];
  prosTitle?: string;
  consTitle?: string;
}) {
  return (
    <div className="not-prose grid md:grid-cols-2 gap-4 my-8">
      <div className="rounded-xl border border-gold/30 bg-gold/5 p-5">
        <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
          <CheckCircle2 size={18} className="text-gold" /> {prosTitle}
        </h3>
        <ul className="space-y-2">
          {pros.map((p) => (
            <li key={p} className="flex gap-2 text-sm text-foreground/85">
              <span className="text-gold mt-0.5">+</span>
              <span>{p}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="rounded-xl border border-border bg-bbn-neutral p-5">
        <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
          <XCircle size={18} className="text-muted-foreground" /> {consTitle}
        </h3>
        <ul className="space-y-2">
          {cons.map((c) => (
            <li key={c} className="flex gap-2 text-sm text-foreground/85">
              <span className="text-muted-foreground mt-0.5">−</span>
              <span>{c}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export type ProcessStep = { title: string; body: string };

/** Numrerad process — renderas som <ol> så det också blir HowTo-vänligt. */
export function StepProcess({
  steps,
  title,
}: {
  steps: ProcessStep[];
  title?: string;
}) {
  return (
    <section className="not-prose my-8" aria-label={title ?? "Process"}>
      {title && (
        <h3 className="font-display text-2xl text-foreground mb-4">{title}</h3>
      )}
      <ol className="space-y-3">
        {steps.map((s, i) => (
          <li
            key={s.title}
            className="relative rounded-xl border border-border bg-card p-5 pl-16"
          >
            <span
              aria-hidden
              className="absolute left-4 top-5 inline-flex h-9 w-9 items-center justify-center rounded-full bg-navy text-cream font-display text-lg"
            >
              {i + 1}
            </span>
            <h4 className="font-semibold text-foreground mb-1">{s.title}</h4>
            <p className="text-sm text-foreground/80 leading-relaxed m-0">{s.body}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

/** Myt vs fakta — vanligaste missuppfattningarna i klartext. */
export function MythVsFact({
  items,
}: {
  items: { myth: string; fact: string }[];
}) {
  return (
    <div className="not-prose my-8 space-y-3">
      {items.map((m) => (
        <div
          key={m.myth}
          className="rounded-xl border border-border bg-card overflow-hidden"
        >
          <div className="px-5 py-3 bg-bbn-neutral border-b border-border">
            <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1">
              Myt
            </p>
            <p className="text-foreground/85 m-0">{m.myth}</p>
          </div>
          <div className="px-5 py-3">
            <p className="text-xs uppercase tracking-wider text-gold font-semibold mb-1">
              Fakta
            </p>
            <p className="text-foreground m-0">{m.fact}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Färgad info-/varningsbox för viktiga klargöranden. */
export function Callout({
  variant = "info",
  title,
  children,
}: {
  variant?: "info" | "warning" | "tip";
  title: string;
  children: ReactNode;
}) {
  const styles =
    variant === "warning"
      ? "border-destructive/30 bg-destructive/5"
      : variant === "tip"
      ? "border-gold/30 bg-gold/5"
      : "border-bbn-primary/30 bg-bbn-primary/5";
  const Icon = variant === "warning" ? AlertTriangle : variant === "tip" ? Sparkles : Info;
  return (
    <aside className={`not-prose my-6 rounded-xl border p-5 ${styles}`}>
      <p className="font-semibold text-foreground flex items-center gap-2 mb-2 m-0">
        <Icon size={16} className="text-gold" /> {title}
      </p>
      <div className="text-sm text-foreground/85 leading-relaxed [&>p]:m-0 [&>p+p]:mt-2">
        {children}
      </div>
    </aside>
  );
}

/** Case-/exempelkort — konkretiserar med ett verkligt scenario. */
export function ExampleCase({
  label = "Räkneexempel",
  title,
  rows,
  outcome,
}: {
  label?: string;
  title: string;
  rows: { k: string; v: string }[];
  outcome: string;
}) {
  return (
    <figure className="not-prose my-8 rounded-2xl border border-border bg-bbn-neutral overflow-hidden">
      <figcaption className="px-5 py-3 bg-navy text-cream flex items-center gap-2">
        <TrendingUp size={16} className="text-gold" />
        <span className="text-xs uppercase tracking-[0.2em] font-semibold">{label}</span>
        <span className="text-cream/70 text-sm">· {title}</span>
      </figcaption>
      <dl className="divide-y divide-border">
        {rows.map((r) => (
          <div key={r.k} className="flex justify-between gap-4 px-5 py-3 text-sm">
            <dt className="text-foreground/70">{r.k}</dt>
            <dd className="text-foreground font-semibold text-right">{r.v}</dd>
          </div>
        ))}
      </dl>
      <div className="px-5 py-4 bg-gold/10 border-t border-gold/30">
        <p className="m-0 text-sm text-foreground flex items-start gap-2">
          <Quote size={14} className="text-gold mt-1 shrink-0" />
          <span>
            <strong>Resultat:</strong> {outcome}
          </span>
        </p>
      </div>
    </figure>
  );
}

/** Responsivt tabellomslag — håller breda jämförelsetabeller läsbara på mobil. */
export function ResponsiveTable({ children }: { children: ReactNode }) {
  return (
    <div className="not-prose my-8 overflow-x-auto rounded-xl border border-border">
      <table className="w-full text-sm">{children}</table>
    </div>
  );
}

/** Bygger JSON-LD scripts: Article + FAQPage + BreadcrumbList. */
export function buildGuideSchemas(meta: GuideMeta) {
  const scripts: Array<{ type: string; children: string }> = [];
  const baseUrl = "https://bbnnordic.se";
  const absoluteUrl = `${baseUrl}${meta.path}`;
  scripts.push({
    type: "application/ld+json",
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: meta.title,
      description: meta.tldr,
      datePublished: meta.updated,
      dateModified: meta.updated,
      author: { "@type": "Organization", name: "BBN Nordic AB" },
      publisher: {
        "@type": "Organization",
        name: "BBN Nordic AB",
        logo: { "@type": "ImageObject", url: `${baseUrl}/og-default.jpg` },
      },
      mainEntityOfPage: absoluteUrl,
      speakable: {
        "@type": "SpeakableSpecification",
        cssSelector: ["h1", "[data-speakable='intro']", "[data-speakable='tldr']"],
      },
      ...(meta.heroImage ? { image: meta.heroImage } : {}),
    }),
  });
  scripts.push({
    type: "application/ld+json",
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Hem", item: `${baseUrl}/` },
        { "@type": "ListItem", position: 2, name: "Guider", item: `${baseUrl}/guider` },
        { "@type": "ListItem", position: 3, name: meta.title, item: absoluteUrl },
      ],
    }),
  });
  if (meta.faqs && meta.faqs.length > 0) {
    scripts.push({
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: meta.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: f.a,
            speakable: { "@type": "SpeakableSpecification", cssSelector: [".faq-answer"] },
          },
        })),
        speakable: { "@type": "SpeakableSpecification", cssSelector: [".faq-answer"] },
      }),
    });
  }
  if (meta.howTo && meta.howTo.steps.length > 0) {
    scripts.push({
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "HowTo",
        name: meta.howTo.name,
        step: meta.howTo.steps.map((s, i) => ({
          "@type": "HowToStep",
          position: i + 1,
          name: s.name,
          text: s.text,
        })),
      }),
    });
  }
  return scripts;
}

/** Hjälp för Route.head() — bygger meta + links + scripts. */
export function buildGuideHead(meta: GuideMeta) {
  const baseUrl = "https://bbnnordic.se";
  const absoluteUrl = `${baseUrl}${meta.path}`;
  const fallbackImage = `${baseUrl}/og-default.jpg`;
  const ogImage = meta.heroImage ?? fallbackImage;
  return {
    meta: [
      { title: `${meta.title} | BBN Nordic` },
      { name: "description", content: meta.tldr.slice(0, 158) },
      { property: "og:title", content: meta.title },
      { property: "og:description", content: meta.tldr.slice(0, 158) },
      { property: "og:type", content: "article" },
      { property: "og:url", content: absoluteUrl },
      { property: "og:image", content: ogImage },
    ],
    links: [{ rel: "canonical", href: absoluteUrl }],
    scripts: buildGuideSchemas(meta),
  };
}
